import { z } from "zod";

const eventTimeZoneSchema = z
  .string()
  .min(1)
  .refine((timeZone) => {
    try {
      new Intl.DateTimeFormat("en", { timeZone }).format();
      return true;
    } catch {
      return false;
    }
  }, "Event timezone must be a valid IANA timezone");

// One tile of the hero's number band.
const heroStatTileSchema = z.object({
  value: z.string(),
  label: z.string(),
});

export const publicEventSchema = z.object({
  eventId: z.string(),
  title: z.string(),
  slug: z.string(),
  location: z.string(),
  description: z.string(),
  startDate: z.string(),
  endDate: z.string(),
  // The event's IANA zone, which the agenda prints its times on. It is part of
  // every current public projection; refusing a stale projection is safer
  // than silently showing a participant their device's local clock.
  timezone: eventTimeZoneSchema,
  // Archived by an organiser (TNG-171). The projection writes it on every
  // document from now on; one written before then lacks it, and absent means
  // false. An archived event stays readable by its link.
  archived: z.boolean().optional(),
  // The projection always writes this key and sets it to null when the event
  // has no hero image, so .optional() alone rejects every image-less event.
  image: z.string().nullable().optional(),
  imageFocalX: z.number().optional(),
  imageFocalY: z.number().optional(),
  configAgenda: z.boolean().optional(),
  configSpeakers: z.boolean().optional(),
  configAttendees: z.boolean().optional(),
  configPartners: z.boolean().optional(),
  configVenues: z.boolean().optional(),
  configResources: z.boolean().optional(),
  configInformation: z.boolean().optional(),
  // The "Now" tab (TNG-97). Absent means on, like the agenda it is built from.
  configNow: z.boolean().optional(),
  // The "After the event" section on the Overview (TNG-96). Absent means off;
  // on, it still waits for the event to end on its own clock.
  configAfterEvent: z.boolean().optional(),
  // QR connect and the connections list (TNG-93). Absent means off.
  configConnections: z.boolean().optional(),
  // The page needs to know a door code will be asked for; the code itself
  // never travels in this projection.
  registrationSecretEnabled: z.boolean().optional(),
  // TNG-92: whether attendees may ask for a table switch. Absent means off.
  trackSwitchEnabled: z.boolean().optional(),
  // The landing page of the bold design. The projection writes every key and
  // sets it to null when the event has none, and a projection written before
  // these fields existed lacks them, so each is nullable and optional: both
  // mean the plain hero.
  heroKicker: z.string().nullable().optional(),
  // Up to three lines split by a line break; the last renders in the accent.
  heroHeadline: z.string().nullable().optional(),
  heroSubline: z.string().nullable().optional(),
  // Three fixed slots, each present only when its tile is whole.
  heroStats: z
    .object({
      a: heroStatTileSchema.optional(),
      b: heroStatTileSchema.optional(),
      c: heroStatTileSchema.optional(),
    })
    .nullable()
    .optional(),
  // The download URL of a short muted loop. The poster is `image`.
  heroVideo: z.string().nullable().optional(),
  // A title per programme day, keyed by yyyy-mm-dd.
  dayTitles: z.record(z.string(), z.string()).nullable().optional(),
  updatedAt: z.any().optional(),
});

export type PublicEvent = z.infer<typeof publicEventSchema>;
